import { Macro } from '@chiroup/core/types/Macro';
import { Transition } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  showPopover: boolean;
  setShowPopover: React.Dispatch<React.SetStateAction<boolean>>;
  filteredMacros: Partial<Macro>[];
  handleMacroClick: (phrase: string, cursorPosition?: number) => void;
  cursorPosition?: number;
  placement?: 'top' | 'bottom';
  anchorRef?: React.RefObject<HTMLElement>;
};

const MacrosPopover: React.FC<Props> = ({
  showPopover,
  filteredMacros,
  handleMacroClick,
  cursorPosition,
  setShowPopover,
  placement = 'bottom',
  anchorRef,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null
  );

  // Calculate position based on anchor element
  useLayoutEffect(() => {
    if (showPopover && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 200;

      // Determine if we should show above or below
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldShowAbove =
        placement === 'top' || (spaceBelow < popoverHeight && spaceAbove > spaceBelow);

      setPosition({
        top: shouldShowAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showPopover, anchorRef, placement]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!showPopover || !anchorRef?.current) return;

    const updatePosition = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldShowAbove =
        placement === 'top' || (spaceBelow < popoverHeight && spaceAbove > spaceBelow);

      setPosition({
        top: shouldShowAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showPopover, anchorRef, placement]);

  useEffect(() => {
    if (!filteredMacros.length) {
      setShowPopover(false);
    }
  }, [filteredMacros, setShowPopover]);

  // Click-outside handler to dismiss popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showPopover && popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Also check if click is on the anchor element (input/textarea)
        if (anchorRef?.current && anchorRef.current.contains(e.target as Node)) {
          return;
        }

        setShowPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover, setShowPopover, anchorRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPopover) {
        if (e.code === 'Tab') {
          if (highlightedIndex !== -1) {
            e.preventDefault();
            handleMacroClick(filteredMacros[highlightedIndex]?.phrase ?? '', cursorPosition ?? 0);
          }
        } else if (e.code === 'ArrowDown') {
          const macroLength = filteredMacros.length;
          e.preventDefault();
          setHighlightedIndex(prevIndex => {
            const newIndex = prevIndex + 1;

            return newIndex + 1 > macroLength ? 0 : newIndex;
          });
        } else if (e.code === 'ArrowUp') {
          const macroLength = filteredMacros.length;
          e.preventDefault();
          setHighlightedIndex(prevIndex => {
            const newIndex = prevIndex - 1;

            return newIndex < 0 ? macroLength - 1 : newIndex;
          });
        } else if (e.code === 'Backspace') {
          setShowPopover(false);
        } else if (e.code === 'Escape') {
          setShowPopover(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    highlightedIndex,
    showPopover,
    cursorPosition,
    filteredMacros,
    handleMacroClick,
    setShowPopover,
  ]);

  // If we have an anchor ref, use portal to render outside modal
  const popoverContent = (
    <Transition
      show={showPopover}
      as={React.Fragment}
      enter="transition ease-out duration-200"
      enterFrom="opacity-0 translate-y-1"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-1"
    >
      <div
        ref={popoverRef}
        className="bg-white border border-gray-300 rounded-md shadow-lg flex flex-col"
        style={
          anchorRef
            ? {
                position: 'fixed',
                zIndex: 9999,
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                width: position?.width ?? 'auto',
                maxWidth: '32rem',
                maxHeight: 'calc(100vh - 100px)',
              }
            : {
                position: 'absolute',
                zIndex: 50,
                width: '100%',
                maxWidth: '32rem',
                maxHeight: 'calc(100vh - 100px)',
                ...(placement === 'top'
                  ? { bottom: '100%', marginBottom: '0.5rem' }
                  : { marginTop: '0.5rem' }),
              }
        }
      >
        <p className="italic p-1 text-xs text-gray-500 b-2 flex items-center bg-gray-300 px-2 border border-gray-300 rounded-t-md">
          <span>
            <InformationCircleIcon className="h-4 w-4 mr-2" />
          </span>
          <span>Click on shortcut or press tab to use macro</span>
        </p>
        <div className="p-2 max-h-48 overflow-y-auto">
          <ul className="list-none mt-2">
            {filteredMacros?.map((macro, index) => (
              <li
                key={index}
                className={`cursor-pointer py-1 px-2 ${
                  index === highlightedIndex ? 'bg-gray-100' : ''
                } rounded-md`}
                onClick={() => handleMacroClick(macro.phrase ?? '', cursorPosition)}
              >
                {macro.shortcut}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Transition>
  );

  // Use portal if we have an anchor ref (to escape modal overflow)
  if (anchorRef && showPopover && position) {
    return createPortal(popoverContent, document.body);
  }

  // Fallback to relative positioning if no anchor ref
  return <div className="relative w-full">{popoverContent}</div>;
};

export default MacrosPopover;
